import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

// Actions
import {
  checkCampaignMediasMetricsComputed,
  updateCampaignState,
} from "../../../actions/campaign/CampaignBasicInfoActions";
import { downloadCampaignExcelReport } from "../../../actions/campaign-plan/CampaignPlanActions";

// Constants And Utils
import { constructRedirectPath } from "../../../utils/redirect-utils/RedirectUtils";
import { ProohFormDataTargets } from "../../constants/GeneralConstants";
import { formatText } from "../../../common-utils/string-utils/StringUtils";
import { checkIsDigitalCampaign } from "../../utils/campaignPlanningUtil";

// Components
import Spinner from "../../../components/spinner/Spinner";

// Other page Sections
import { CampaignState, CampaignStateText } from "./planningConstants";

function StatusIcon({ displayText, textTypeClass = "", iconClass = "" }) {
  return (
    <div className={`d-flex align-items-center ml-2 ${textTypeClass}`}>
      <i className={`fas ${iconClass}`}></i>
      <span className="ml-1">{displayText}</span>
    </div>
  );
}

export function CampaignStatus({ state }) {
  if (state === CampaignState.DRAFT) {
    return (
      <StatusIcon
        displayText={CampaignStateText.DRAFT}
        textTypeClass="text-warning"
        iconClass="fa-exclamation-triangle"
      />
    );
  }

  if (state === CampaignState.REVIEW) {
    return (
      <StatusIcon
        displayText={CampaignStateText.REVIEW}
        textTypeClass="text-warning"
        iconClass="fa-exclamation-triangle"
      />
    );
  }

  if (state === CampaignState.CONFIRMED) {
    return (
      <StatusIcon
        displayText={CampaignStateText.CONFIRMED}
        iconClass="fa-clock"
      />
    );
  }

  if (state === CampaignState.SCHEDULED) {
    return (
      <StatusIcon
        displayText={CampaignStateText.SCHEDULED}
        iconClass="fa-clock"
      />
    );
  }

  if (state === CampaignState.LIVE) {
    return (
      <StatusIcon
        displayText={CampaignStateText.LIVE}
        textTypeClass="text-success"
        iconClass="fa-video"
      />
    );
  }

  if (state === CampaignState.COMPLETED) {
    return (
      <StatusIcon
        displayText={CampaignStateText.COMPLETED}
        textTypeClass="text-success"
        iconClass="fa-check-circle"
      />
    );
  }

  if (state === CampaignState.CANCELLED) {
    return (
      <StatusIcon
        displayText={CampaignStateText.CANCELLED}
        textTypeClass="text-danger"
        iconClass="fa-window-close"
      />
    );
  }

  if (state === CampaignState.STOPPED) {
    return (
      <StatusIcon
        displayText={CampaignStateText.STOPPED}
        textTypeClass="text-danger"
        iconClass="fa-circle"
      />
    );
  }

  return null;
}

function getClientName(campaign) {
  const { planStatus, buyerName } = campaign;
  if (CampaignState.DRAFT === planStatus) {
    return "NA";
  }
  return buyerName;
}

export function CampaignDetails({ campaign = {} }) {
  if (Object.keys(campaign).length < 1) {
    return null;
  }

  const { title = "New Campaign", planStatus } = campaign;
  const client = getClientName(campaign);

  return (
    <>
      <h3 className="mb-0">{title}</h3>
      <div className="d-flex align-items-center">
        <p className="mb-0">
          <span>Client: {formatText(client)} | </span>
          <span>
            Last Updated: {new Date(campaign.updatedOn).toDateString()} |
          </span>
        </p>
        <CampaignStatus state={planStatus} />
      </div>
    </>
  );
}

export function SelectionReviewCampaign({ campaignId, cityId }) {
  const history = useHistory();

  function redirectToReviewPage() {
    history.push(
      constructRedirectPath(`/campaign/${campaignId}/city/${cityId}/draft`)
    );
  }
  return (
    <button
      type="button"
      className="btn btn-primary m-2"
      onClick={redirectToReviewPage}
    >
      Selection Review
    </button>
  );
}

export function SaveAsDraft() {
  const history = useHistory();

  function redirectToHomePage() {
    toast.success("Successfully Saved as Draft");
    history.push(constructRedirectPath("/home"));
  }

  return (
    <button
      type="button"
      className="btn btn-outline-primary m-2"
      onClick={redirectToHomePage}
    >
      Save as Draft
    </button>
  );
}

export function SendForConfirmation({ confirmAction, isDisabled = false }) {
  // Selector
  const sendForConfirmation = useSelector(
    (state) => state.campaignReview.campaignConfirmLoading
  );

  return (
    <button
      type="button"
      data-toggle="modal"
      disabled={sendForConfirmation || isDisabled}
      data-target={`#${ProohFormDataTargets.sendConfirmationForm}`}
      onClick={confirmAction}
      className="btn btn-primary mx-2"
    >
      <span>Send For Confirmation</span>{" "}
      {sendForConfirmation && (
        <Spinner className="ml-1 text-dark spinner-border-sm" />
      )}
    </button>
  );
}

export function ContinueEditing({ onClickAction }) {
  return (
    <button
      type="button"
      className="btn btn-link shadow-none px-3"
      onClick={onClickAction}
    >
      Continue Editing
    </button>
  );
}

export function AddCityInCampaign({ onClickAction = () => {} }) {
  return (
    <button
      type="button"
      className="btn btn-primary mx-2"
      onClick={onClickAction}
    >
      Add City
    </button>
  );
}

export function ConfirmCampaign({ confirmAction }) {
  // Selector
  const updateStateLoading = useSelector(
    (state) => state.campaignPlan.updateStateLoading
  );

  return (
    <button
      type="button"
      className="btn btn-primary mx-2"
      onClick={confirmAction}
    >
      <span>Confirm</span>{" "}
      {updateStateLoading && (
        <Spinner className="ml-1 text-dark spinner-border-sm" />
      )}
    </button>
  );
}

export function ScheduleCampaign({ scheduleCampaign }) {
  //Selector
  const updateStateLoading = useSelector(
    (state) => state.campaignPlan.updateStateLoading
  );
  return (
    <button
      type="button"
      className="btn btn-primary m-2"
      onClick={scheduleCampaign}
    >
      <span>Schedule</span>{" "}
      {updateStateLoading && (
        <Spinner className="ml-1 text-dark spinner-border-sm" />
      )}
    </button>
  );
}

export function StopCampaign({ campaignId }) {
  const dispatch = useDispatch();
  const history = useHistory();

  return (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() =>
        dispatch(
          updateCampaignState(campaignId, CampaignState.STOPPED, history)
        )
      }
    >
      Stop Campaign
    </button>
  );
}

export function CancelCampaign({ campaignId }) {
  const dispatch = useDispatch();
  const history = useHistory();

  return (
    <button
      type="button"
      className="btn btn-link shadow-none px-3"
      onClick={() =>
        dispatch(
          updateCampaignState(campaignId, CampaignState.CANCELLED, history)
        )
      }
    >
      Cancel Campaign
    </button>
  );
}

export function DownloadCampaignExcelReport({
  campaign = {},
  campaignPlanOverview = {},
}) {
  const dispatch = useDispatch();

  // Selector
  const downloadExcelReportLoading = useSelector(
    (state) => state.campaignPlan.downloadCampaignExcelReportLoading
  );

  const { title, id, isMetricsComputed } = campaign || {};

  // toast message
  const toastMessage = "Excel report data is generated and can be downloaded.";

  // Polling every 15 sec
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isMetricsComputed) {
        dispatch(checkCampaignMediasMetricsComputed(id, toastMessage));
      }
    }, 15000);

    // cleanup
    return () => clearInterval(interval);
  }, [dispatch, isMetricsComputed]);

  // check isDigital campaign
  const isDigitalCampaign = checkIsDigitalCampaign(campaignPlanOverview);

  // functions
  function handleDownloadExcelReport() {
    dispatch(downloadCampaignExcelReport(id, title, isDigitalCampaign));
  }

  // btn label
  const buttonLabel = isMetricsComputed
    ? "Download Excel Report"
    : "Generating Excel Report";

  // is show loader
  const isShowLoader = downloadExcelReportLoading || !isMetricsComputed;

  return (
    <button
      type="button"
      className="btn btn-primary ml-2"
      disabled={isShowLoader}
      onClick={handleDownloadExcelReport}
    >
      <span>{buttonLabel}</span>

      {/* Loading Spinner */}
      {isShowLoader && <Spinner className="ml-1 text-dark spinner-border-sm" />}
    </button>
  );
}
